import React, { useState, useEffect, useCallback } from 'react';
import {
  db,
  collection,
  doc,
  getDocs,
  updateDoc
} from '../firebase';

// Admin-only fleet overview: every device across every portfolio (plus
// unclaimed) in one table — online status, VPN IP, WiFi signal, last
// check-in. The VPN IP is self-reported by each Pi on check-in, so this
// is always current with no hand-maintained list.
function Fleet({ onBack }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [sort, setSort] = useState({ key: 'portfolio', dir: 1 });

  const loadFleet = useCallback(async () => {
    try {
      const rows = [];

      const portfolios = await getDocs(collection(db, 'customer_portfolios'));
      for (const portfolio of portfolios.docs) {
        const portfolioName = portfolio.data().name || portfolio.id;
        const deviceDocs = await getDocs(
          collection(db, 'customer_portfolios', portfolio.id, 'devices')
        );
        deviceDocs.forEach((d) => {
          rows.push({
            id: `${portfolio.id}/${d.id}`,
            serial: d.id,
            portfolio: portfolioName,
            portfolioId: portfolio.id,
            unclaimed: false,
            ...d.data()
          });
        });
      }

      const unclaimedDocs = await getDocs(collection(db, 'unclaimed_devices'));
      unclaimedDocs.forEach((d) => {
        rows.push({
          id: `unclaimed/${d.id}`,
          serial: d.id,
          name: d.id,
          portfolio: null,
          unclaimed: true,
          ...d.data()
        });
      });

      rows.sort((a, b) =>
        (a.portfolio || 'zzz').localeCompare(b.portfolio || 'zzz') ||
        (a.name || '').localeCompare(b.name || '')
      );

      setDevices(rows);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading fleet:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount, refresh every 60s while the page is open
  useEffect(() => {
    loadFleet();
    const interval = setInterval(loadFleet, 60000);
    return () => clearInterval(interval);
  }, [loadFleet]);

  const lastSeenMs = (device) => {
    const ts = device.last_seen;
    if (!ts) return 0;
    return ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
  };

  // Claimed devices carry the server-maintained isOffline flag; unclaimed
  // ones don't, so fall back to "checked in within 5 minutes".
  const isOnline = (device) => {
    if (!device.unclaimed && device.isOffline !== undefined) return !device.isOffline;
    return Date.now() - lastSeenMs(device) < 5 * 60 * 1000;
  };

  const relativeTime = (device) => {
    const ms = lastSeenMs(device);
    if (!ms) return 'never';
    const mins = Math.floor((Date.now() - ms) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const copyIp = (ip) => {
    if (!ip) return;
    navigator.clipboard.writeText(ip).catch(() => {});
  };

  const startEditId = (device) => {
    setEditingId(device.id);
    setEditValue(device.device_id || '');
  };

  // Short admin label ("pi15") stored on the device doc. Also becomes the
  // device's Ansible hostname via /api/fleet.
  const saveDeviceId = async (device) => {
    const value = editValue.trim();
    setEditingId(null);
    if (value === (device.device_id || '')) return;
    try {
      const ref = device.unclaimed
        ? doc(db, 'unclaimed_devices', device.serial)
        : doc(db, 'customer_portfolios', device.portfolioId, 'devices', device.serial);
      await updateDoc(ref, { device_id: value });
      setDevices((prev) =>
        prev.map((d) => (d.id === device.id ? { ...d, device_id: value } : d))
      );
    } catch (error) {
      console.error('Error saving device ID:', error);
      alert('Failed to save device ID: ' + error.message);
    }
  };

  const wifiClass = (percent) => {
    if (percent >= 70) return 'wifi-good';
    if (percent >= 40) return 'wifi-ok';
    return 'wifi-bad';
  };

  // vpn_cert_expires is epoch seconds, self-reported by each Pi
  const certDaysLeft = (device) => {
    if (!device.vpn_cert_expires) return null;
    return Math.floor((device.vpn_cert_expires * 1000 - Date.now()) / 86400000);
  };

  const certClass = (days) => {
    if (days < 30) return 'cert-bad';
    if (days < 90) return 'cert-warn';
    return 'cert-good';
  };

  const certLabel = (days) => {
    if (days < 0) return 'EXPIRED';
    if (days < 60) return `${days}d`;
    if (days < 730) return `${Math.round(days / 30)}mo`;
    return `${Math.round(days / 365)}yr`;
  };

  const onlineCount = devices.filter(isOnline).length;

  // Sortable columns: how to extract a comparable value per key
  const sortValue = (device, key) => {
    switch (key) {
      case 'status': return isOnline(device) ? 1 : 0;
      case 'device_id': return (device.device_id || '￿').toLowerCase();
      case 'name': return (device.name || device.serial || '').toLowerCase();
      case 'portfolio': return (device.portfolio || '￿').toLowerCase();
      case 'vpn_ip':
        // pad octets so 10.8.0.9 sorts before 10.8.0.15
        return device.vpn_ip
          ? device.vpn_ip.split('.').map((n) => n.padStart(3, '0')).join('.')
          : '￿';
      case 'wifi': return device.wifi_signal?.percent ?? -1;
      case 'cert': return device.vpn_cert_expires || Number.MAX_SAFE_INTEGER;
      case 'last_seen': return lastSeenMs(device);
      case 'sensors': return device.sensors?.length || 0;
      case 'serial': return device.serial || '';
      default: return 0;
    }
  };

  const sortedDevices = [...devices].sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return cmp * sort.dir;
  });

  const toggleSort = (key) => {
    setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  };

  const Th = ({ k, children }) => (
    <th className="sortable" onClick={() => toggleSort(k)}>
      {children}
      <span className="sort-arrow">
        {sort.key === k ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
      </span>
    </th>
  );

  if (loading) {
    return <div className="loading">Loading fleet...</div>;
  }

  return (
    <div className="fleet">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}>&larr; Back</button>
        <h2>Fleet</h2>
        <div className="fleet-summary">
          <span className="dot online" /> {onlineCount} online
          <span className="dot offline" /> {devices.length - onlineCount} offline
          <button className="btn-secondary" onClick={loadFleet}>Refresh</button>
        </div>
      </div>

      <div className="fleet-table-wrap">
        <table className="fleet-table">
          <thead>
            <tr>
              <Th k="status"></Th>
              <Th k="device_id">Device ID</Th>
              <Th k="name">Device</Th>
              <Th k="portfolio">Portfolio</Th>
              <Th k="vpn_ip">VPN IP</Th>
              <Th k="wifi">WiFi</Th>
              <Th k="cert">VPN Cert</Th>
              <Th k="last_seen">Last Seen</Th>
              <Th k="sensors">Sensors</Th>
              <Th k="serial">Serial</Th>
            </tr>
          </thead>
          <tbody>
            {sortedDevices.map((device) => (
              <tr key={device.id} className={isOnline(device) ? '' : 'row-offline'}>
                <td>
                  <span className={`dot ${isOnline(device) ? 'online' : 'offline'}`} />
                </td>
                <td>
                  {editingId === device.id ? (
                    <input
                      className="device-id-input"
                      value={editValue}
                      autoFocus
                      placeholder="e.g. pi15"
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveDeviceId(device)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveDeviceId(device);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span
                      className={`device-id ${device.device_id ? '' : 'unset'}`}
                      title="Click to edit"
                      onClick={() => startEditId(device)}
                    >
                      {device.device_id || 'set id'}
                    </span>
                  )}
                </td>
                <td className="cell-name">{device.name || device.serial}</td>
                <td>
                  {device.portfolio || <span className="badge-unclaimed">Unclaimed</span>}
                </td>
                <td>
                  {device.vpn_ip ? (
                    <code
                      className="vpn-ip"
                      title="Click to copy"
                      onClick={() => copyIp(device.vpn_ip)}
                    >
                      {device.vpn_ip}
                    </code>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {device.wifi_signal?.percent != null ? (
                    <span className={wifiClass(device.wifi_signal.percent)}>
                      {device.wifi_signal.percent}%
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {certDaysLeft(device) != null ? (
                    <span
                      className={certClass(certDaysLeft(device))}
                      title={new Date(device.vpn_cert_expires * 1000).toLocaleDateString()}
                    >
                      {certLabel(certDaysLeft(device))}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{relativeTime(device)}</td>
                <td>{device.sensors?.length || 0}</td>
                <td><code className="serial">{device.serial}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
        {devices.length === 0 && (
          <div className="empty-state">
            <p>No devices registered yet</p>
          </div>
        )}
      </div>

      {lastRefresh && (
        <p className="refresh-note">
          Updated {lastRefresh.toLocaleTimeString()} — refreshes every minute
        </p>
      )}

      <style>{`
        .fleet {
          padding: 20px;
          background: #16213e;
          border-radius: 12px;
          color: #eee;
        }
        .fleet .page-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }
        .fleet .page-header h2 {
          margin: 0;
          flex: 1;
        }
        .fleet-summary {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #bbb;
        }
        .fleet-summary .btn-secondary {
          margin-left: 12px;
        }
        .fleet .dot {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin: 0 4px;
        }
        .fleet .dot.online { background: #2ecc71; }
        .fleet .dot.offline { background: #e74c3c; }
        .fleet-table-wrap {
          overflow-x: auto;
        }
        .fleet-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .fleet-table th {
          text-align: left;
          padding: 10px 12px;
          color: #8fa3c8;
          font-weight: 600;
          border-bottom: 2px solid #0f3460;
          white-space: nowrap;
        }
        .fleet-table th.sortable {
          cursor: pointer;
          user-select: none;
        }
        .fleet-table th.sortable:hover {
          color: #c8d6f0;
        }
        .sort-arrow {
          font-size: 10px;
          color: #4a90d9;
        }
        .fleet-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #1f2b4d;
          white-space: nowrap;
        }
        .fleet-table .row-offline td {
          opacity: 0.6;
        }
        .cell-name {
          font-weight: 600;
        }
        .device-id {
          background: #0f3460;
          padding: 3px 8px;
          border-radius: 4px;
          cursor: pointer;
          font-family: monospace;
          font-weight: 600;
        }
        .device-id:hover {
          background: #1a4a80;
        }
        .device-id.unset {
          background: transparent;
          border: 1px dashed #33507a;
          color: #667;
          font-weight: 400;
          font-style: italic;
        }
        .device-id-input {
          background: #0f3460;
          border: 1px solid #4a90d9;
          border-radius: 4px;
          color: #eee;
          padding: 3px 8px;
          font-family: monospace;
          width: 90px;
        }
        .vpn-ip {
          background: #0f3460;
          padding: 3px 8px;
          border-radius: 4px;
          cursor: pointer;
          font-family: monospace;
        }
        .vpn-ip:hover {
          background: #1a4a80;
        }
        .serial {
          font-family: monospace;
          font-size: 12px;
          color: #8fa3c8;
        }
        .badge-unclaimed {
          background: #f39c12;
          color: #1a1a2e;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }
        .wifi-good { color: #2ecc71; }
        .wifi-ok { color: #f39c12; }
        .wifi-bad { color: #e74c3c; }
        .cert-good { color: #2ecc71; }
        .cert-warn { color: #f39c12; font-weight: 600; }
        .cert-bad { color: #e74c3c; font-weight: 700; }
        .fleet .muted { color: #556; }
        .refresh-note {
          margin: 12px 0 0;
          font-size: 12px;
          color: #667;
        }
        .fleet .empty-state {
          text-align: center;
          padding: 40px;
          color: #888;
        }
      `}</style>
    </div>
  );
}

export default Fleet;
